import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { resolvePhotoUrl } from '../utils/photoUrl';
import ConfirmDeleteModal from './ConfirmDeleteModal';

/*
 * A person's own Identify-game reviews and AI feedback, each resettable one at a
 * time or all at once.
 *
 * Resetting deletes only the caller's own rows (routes/image_quality.py,
 * routes/ai.py). identification_history -- the official record -- is never
 * touched, and every dialog says so. AI feedback is listed only for roles that
 * can give it; for anyone else the section is simply not shown.
 */
const PAGE = 20;
const EMPTY = { items: [], total: 0, status: 'loading' };

export default function MyAiHistory({ onChanged }) {
  const { t } = useTranslation('game');
  const [reviews, setReviews] = useState(EMPTY);
  const [feedback, setFeedback] = useState(EMPTY);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadReviews = useCallback((offset = 0) => Promise.resolve(
    api.get('/ai/quality/annotations/mine', { params: { limit: PAGE, offset } }))
    .then(({ data }) => setReviews(prev => ({
      items: offset ? [...prev.items, ...(data.items || [])] : (data.items || []),
      total: data.total || 0, status: 'ready',
    })))
    .catch(() => setReviews(prev => ({ ...prev, status: 'failed' }))), []);

  const loadFeedback = useCallback((offset = 0) => Promise.resolve(
    api.get('/ai/feedback/mine', { params: { limit: PAGE, offset } }))
    .then(({ data }) => setFeedback(prev => ({
      items: offset ? [...prev.items, ...(data.items || [])] : (data.items || []),
      total: data.total || 0, status: 'ready',
    })))
    .catch(err => setFeedback(prev => ({
      ...prev, status: err?.response?.status === 403 ? 'unavailable' : 'failed',
    }))), []);

  useEffect(() => {
    loadReviews(0);
    loadFeedback(0);
  }, [loadReviews, loadFeedback]);

  const dialogs = {
    review: item => ({ title: t('history.confirmOneTitle'), body: t('history.confirmOneBody'),
                       run: () => api.delete(`/ai/quality/annotations/${item.annotation_id}`),
                       done: () => setReviews(r => ({ ...r, items: r.items.filter(i => i.annotation_id !== item.annotation_id),
                                                      total: Math.max(0, r.total - 1) })) }),
    reviews: () => ({ title: t('history.confirmAllReviewsTitle'),
                      body: t('history.confirmAllReviewsBody', { count: reviews.total }),
                      run: () => api.delete('/ai/quality/annotations/mine', { params: { confirm: 'all' } }),
                      done: () => setReviews({ items: [], total: 0, status: 'ready' }) }),
    feedback: item => ({ title: t('history.confirmFeedbackTitle'), body: t('history.confirmFeedbackBody'),
                         run: () => api.delete(`/ai/feedback/${item.feedback_id}`),
                         done: () => setFeedback(f => ({ ...f, items: f.items.filter(i => i.feedback_id !== item.feedback_id),
                                                         total: Math.max(0, f.total - 1) })) }),
    feedbacks: () => ({ title: t('history.confirmAllFeedbackTitle'),
                        body: t('history.confirmAllFeedbackBody', { count: feedback.total }),
                        run: () => api.delete('/ai/feedback/mine', { params: { confirm: 'all' } }),
                        done: () => setFeedback({ items: [], total: 0, status: 'ready' }) }),
  };
  const dialog = confirm ? dialogs[confirm.kind](confirm.item) : null;

  const doReset = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await dialog.run();
      dialog.done();
      setConfirm(null);
      if (onChanged) onChanged(res?.data?.me);
    } catch {
      setError(t('history.resetFailed'));
    } finally {
      setBusy(false);
    }
  };

  const open = (kind, item) => { setError(''); setConfirm({ kind, item }); };

  return (
    <section data-testid="my-ai-history" style={sectionStyle}>
      <h3 style={h3Style}>{t('history.reviewsTitle')} ({reviews.total})</h3>
      {reviews.status === 'failed' && <p role="alert" style={errorStyle}>{t('history.loadFailed')}</p>}
      {reviews.status === 'ready' && reviews.items.length === 0 && <p style={mutedStyle}>{t('history.empty')}</p>}
      <ul style={listStyle}>
        {reviews.items.map(item => (
          <li key={item.annotation_id} data-testid="history-review" style={rowStyle}>
            {item.s3_url && <img src={resolvePhotoUrl(item.s3_url)} alt="" style={thumbStyle} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{t(`verdict.${item.verdict}`)}</div>
              <div style={mutedStyle}>
                {item.dataset_label && <span dir="ltr">{t('history.label', { label: item.dataset_label })} · </span>}
                {item.guessed_genus && <span dir="ltr">{t('history.guess', { guess: item.guessed_genus })} · </span>}
                {item.saw_ai_first && <span>{t('history.hintUsed')} · </span>}
                {item.label_verdict && <span>{t('history.labelVerdict', { verdict: t(`reveal.labelVerdict.${item.label_verdict}`) })}</span>}
              </div>
            </div>
            <button type="button" onClick={() => open('review', item)} title={t('tip.history.resetReview')} style={smallBtn}>{t('history.reset')}</button>
          </li>
        ))}
      </ul>
      <div style={actionsStyle}>
        {reviews.items.length < reviews.total && (
          <button type="button" onClick={() => loadReviews(reviews.items.length)} title={t('tip.history.loadMore')} style={linkBtn}>{t('history.loadMore')}</button>
        )}
        {reviews.total > 0 && (
          <button type="button" data-testid="reset-all-reviews" onClick={() => open('reviews')} title={t('tip.history.resetAllReviews')} style={dangerBtn}>
            {t('history.resetAllReviews')}
          </button>
        )}
      </div>

      {feedback.status !== 'unavailable' && (
        <>
          <h3 style={h3Style}>{t('history.feedbackTitle')} ({feedback.total})</h3>
          {feedback.status === 'failed' && <p role="alert" style={errorStyle}>{t('history.loadFailed')}</p>}
          {feedback.status === 'ready' && feedback.items.length === 0 && <p style={mutedStyle}>{t('history.empty')}</p>}
          <ul style={listStyle}>
            {feedback.items.map(item => (
              <li key={item.feedback_id} data-testid="history-feedback" style={rowStyle}>
                {item.image_url && <img src={resolvePhotoUrl(item.image_url)} alt="" style={thumbStyle} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{t('history.agreement', { agreement: item.agreement })}</div>
                  <div style={mutedStyle}>
                    {item.top_taxon && <span dir="ltr">{t('history.aiSaid', { taxon: item.top_taxon })}</span>}
                    {item.saw_ai_first && <span> · {t('history.hintUsed')}</span>}
                  </div>
                </div>
                <button type="button" onClick={() => open('feedback', item)} title={t('tip.history.resetFeedback')} style={smallBtn}>{t('history.reset')}</button>
              </li>
            ))}
          </ul>
          <div style={actionsStyle}>
            {feedback.items.length < feedback.total && (
              <button type="button" onClick={() => loadFeedback(feedback.items.length)} title={t('tip.history.loadMore')} style={linkBtn}>{t('history.loadMore')}</button>
            )}
            {feedback.total > 0 && (
              <button type="button" data-testid="reset-all-feedback" onClick={() => open('feedbacks')} title={t('tip.history.resetAllFeedback')} style={dangerBtn}>
                {t('history.resetAllFeedback')}
              </button>
            )}
          </div>
        </>
      )}

      <ConfirmDeleteModal
        open={Boolean(dialog)}
        title={dialog?.title}
        consequences={dialog ? [dialog.body, t('history.officialUntouched')] : []}
        deleting={busy}
        error={error}
        confirmLabel={t('history.reset')}
        confirmingLabel={t('history.resetting')}
        onConfirm={doReset}
        onCancel={() => { if (!busy) setConfirm(null); }}
      />
    </section>
  );
}

const sectionStyle = { border: '1px solid var(--c-border)', borderRadius: 10, padding: 12, background: 'var(--c-surface)', marginTop: 12 };
const h3Style = { margin: '8px 0 6px', fontSize: 15 };
const mutedStyle = { fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '2px 0' };
const errorStyle = { fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', borderRadius: 6, padding: '6px 10px' };
const listStyle = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 };
const rowStyle = { display: 'flex', gap: 10, alignItems: 'center', padding: 6, border: '1px solid var(--c-bg-muted)', borderRadius: 8, fontSize: 13 };
const thumbStyle = { width: 56, height: 42, objectFit: 'cover', borderRadius: 4, flexShrink: 0 };
const actionsStyle = { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 };
const smallBtn = { background: 'var(--c-surface)', color: 'var(--c-danger-2-fg)', border: '1px solid var(--c-danger-border)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 };
const dangerBtn = { background: 'var(--c-danger-2)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 };
const linkBtn = { background: 'none', color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 };
